import React, { useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Filter } from "lucide-react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import SearchInput from "@/components/SearchInput/SearchInput";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { CourseCard } from "@/modules/learningCenter/features/CourseDirectory/components/CourseCard/CourseCard";
import {
  CourseFilterSidebar,
  CourseFilterSidebarBody,
} from "@/modules/learningCenter/features/CourseDirectory/components/CourseFilterSidebar/CourseFilterSidebar";
import { CourseDirectorySkeleton } from "@/modules/learningCenter/features/CourseDirectory/components/CourseDirectory/CourseDirectorySkeleton";
import { useCourseDirectory } from "@/modules/learningCenter/features/CourseDirectory/hooks/useCourseDirectory";

export function CourseDirectory() {
  const {
    searchInput,
    setSearchInput,
    filters,
    toggleFilterValue,
    clearFilters,
    hasActiveFilters,
    items,
    hasMore,
    totalCount,
    isLoading,
    isInitialLoading,
    loadMore,
    metadata,
  } = useCourseDirectory();

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Full-page skeleton on the very first load; subsequent search/filter loads
  // keep the grid and show inline loaders.
  if (isInitialLoading) {
    return (
      <MainPageWrapper
        subtitle="Browse and enroll in courses to build your skills"
        title="Courses"
      >
        <CourseDirectorySkeleton />
      </MainPageWrapper>
    );
  }

  return (
    <MainPageWrapper
      actions={
        <Sheet onOpenChange={setMobileFiltersOpen} open={mobileFiltersOpen}>
          <SheetTrigger asChild>
            <Button
              aria-label="Open filters"
              className="md:hidden"
              size="icon"
              variant="outline"
            >
              <Filter className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            className="w-80 overflow-y-auto bg-white dark:bg-slate-900"
            side="left"
          >
            <SheetHeader>
              <SheetTitle>Filters</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <CourseFilterSidebarBody
                filters={filters}
                metadata={metadata}
                onToggle={toggleFilterValue}
              />
            </div>
          </SheetContent>
        </Sheet>
      }
      subtitle="Browse and enroll in courses to build your skills"
      title="Courses"
    >
      {/* Toolbar — full-width search + result count, above the two columns. */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex-1">
          <SearchInput
            onChange={setSearchInput}
            placeholder="Search courses…"
            value={searchInput}
          />
        </div>
        <div className="flex shrink-0 items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          {totalCount > 0 && (
            <span>
              {totalCount} {totalCount === 1 ? "course" : "courses"}
            </span>
          )}
          {hasActiveFilters && (
            <button
              className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              onClick={clearFilters}
              type="button"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Filter sidebar — sticky with its own scroll on desktop. */}
        <div className="hidden md:block w-64 shrink-0 self-start sticky top-0 max-h-[calc(100vh-8rem)] overflow-y-auto">
          <CourseFilterSidebar
            filters={filters}
            metadata={metadata}
            onToggle={toggleFilterValue}
          />
        </div>

        {/* Results */}
        <div className="flex-1 min-w-0">
          {isLoading && items.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              Loading courses…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No courses found matching your criteria.
            </div>
          ) : (
            <InfiniteScroll
              dataLength={items.length}
              endMessage={
                <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                  No more courses to load
                </div>
              }
              hasMore={hasMore}
              loader={
                <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                  Loading more…
                </div>
              }
              next={loadMore}
              scrollableTarget="main-scroll-area"
            >
              <div className="grid gap-5 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
                {items.map((course) => (
                  <CourseCard course={course} key={course.uuid} />
                ))}
              </div>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </MainPageWrapper>
  );
}

export default CourseDirectory;
